/**
 * OpenStreetMap building tags, read from the Overpass API.
 *
 * The streamed Shortbread tiles carry building *outlines* only: no type, no
 * height, no storeys. OSM itself has all of that — in central Borås 92% of
 * buildings carry a specific `building=*` type, 58% a `start_date`, 11% a real
 * `height` and 11% a `roof:shape`. Overpass serves those tags keyless, with
 * CORS, anywhere on Earth, so this module asks it for one compact table per
 * area and the vector pipeline matches its rows to outlines by position.
 *
 * The query asks for CSV rather than JSON: one tab-separated line a building,
 * ~52 bytes, against ~400 for the JSON with the same columns. A dense town
 * block of ten thousand buildings is half a megabyte, parsed with `split`.
 *
 * Pure: no fetch, no DOM. `loaders/buildingTagService.ts` does the network.
 */

import type { TileBounds } from '../geo/tilekey.js';
import type { LmBuilding } from './lmbuildings.js';

/** One OSM building's tags, trimmed to what shapes a house. */
export interface BuildingTags {
  /** Centre of the way's bounding box, as Overpass computes it. */
  lat: number;
  lon: number;
  /** `building=*`: `house`, `apartments`, `shed`, `church`, `yes`... */
  building: string;
  /** Overall height, ground to top of roof, metres. */
  height?: number;
  /** Height at which the building starts: a walkway over a road, a canopy. */
  minHeight?: number;
  /** Storeys above ground, not counting the roof. */
  levels?: number;
  /** Storeys inside the roof: attics and dormers. */
  roofLevels?: number;
  /** `roof:shape=*` verbatim: `gabled`, `hipped`, `flat`, `skillion`... */
  roofShape?: string;
  /** Height of the roof alone, metres. */
  roofHeight?: number;
  /** Construction year, from `start_date`, as a best single year. */
  year?: number;
  /** `building:colour`, as 0xRRGGBB sRGB. */
  colour?: number;
  /** `roof:colour`, as 0xRRGGBB sRGB. */
  roofColour?: number;
  /** `building:material`: `brick`, `wood`, `concrete`, `plaster`... */
  material?: string;
  /** `roof:material`: `roof_tiles`, `metal`, `slate`, `grass`... */
  roofMaterial?: string;
  /** `amenity=*`, for the places of worship tagged only `building=yes`. */
  amenity?: string;
  /**
   * Set on a staircase's row rather than a building's — its `building` is
   * empty. The tile's `steps` lines carry neither of these.
   */
  steps?: StepsTags;
  /** Set on a charging station's row, not a building's: somewhere to charge an electric car. */
  charger?: true;
  /**
   * On a Sweden tile, Lantmäteriet's class for the footprint (Topografi 10,
   * `core/sources/lmbuildings.ts`). What OSM leaves unsaid — `building=yes`,
   * or no OSM building at all — is read from it; a specific OSM type wins.
   */
  lm?: LmBuilding;
}

/** A staircase's tags: how many steps, and which way is up. */
export interface StepsTags {
  /** `step_count`: with ~17 cm a step, how far the flight climbs. */
  count?: number;
  /** `incline=up` climbs the way's direction, `down` against it. */
  incline?: 'up' | 'down';
}

/**
 * CSV columns, in query order. The parser indexes by position, so the two
 * must stay in step — they are one list for that reason.
 */
export const OVERPASS_COLUMNS = [
  '::type', '::lat', '::lon', 'building', 'height', 'min_height', 'building:levels',
  'roof:levels', 'roof:shape', 'roof:height', 'start_date', 'building:colour', 'roof:colour',
  'building:material', 'roof:material', 'amenity', 'highway', 'step_count', 'incline',
] as const;

/**
 * The Overpass QL for every building way and multipolygon in a box.
 *
 * `out tags center` sends the tags and one point per building — the centre of
 * its bounding box — rather than its geometry, which the vector tile already
 * has. Parts (`building:part`) are left out: they describe a building's
 * pieces, and matching them to whole outlines would stack a tower's height on
 * a church's nave.
 */
export function overpassBuildingQuery(bounds: TileBounds, timeoutSeconds = 25): string {
  const columns = OVERPASS_COLUMNS.map(c => (c.startsWith('::') ? c : `"${c}"`)).join(',');
  const box = [bounds.south, bounds.west, bounds.north, bounds.east].map(v => v.toFixed(6)).join(',');
  return `[out:csv(${columns};false;"\\t")][timeout:${timeoutSeconds}];` +
    `(way["building"](${box});relation["building"]["type"="multipolygon"](${box});` +
    // Staircases with a count of steps or a way up: how high they climb, and
    // which end is the top, neither of which the tile can say.
    `way["highway"="steps"]["step_count"](${box});way["highway"="steps"]["incline"](${box});` +
    // Charging stations, which the tile's points leave out.
    `nwr["amenity"="charging_station"](${box}););out tags center;`;
}

export function overpassUrl(endpoint: string, bounds: TileBounds): string {
  return `${endpoint}?data=${encodeURIComponent(overpassBuildingQuery(bounds))}`;
}

/** Parse the CSV Overpass returns for `overpassBuildingQuery`: buildings, and staircases. */
export function parseOverpassCsv(text: string): BuildingTags[] {
  const out: BuildingTags[] = [];
  let start = 0;
  while (start < text.length) {
    let end = text.indexOf('\n', start);
    if (end < 0) end = text.length;
    const line = text.slice(start, end);
    start = end + 1;
    if (line.length === 0) continue;
    const tags = tagsFromRow(line.split('\t'));
    if (tags !== undefined) out.push(tags);
  }
  return out;
}

/**
 * One row's fields, in `OVERPASS_COLUMNS` order: a building, a staircase or a
 * charging station, or undefined for a row worth nothing. Also how the Sweden
 * tiles' `osm_tags` points are read (`core/sources/swedentiles.ts`).
 */
export function tagsFromRow(f: readonly string[]): BuildingTags | undefined {
  const lat = Number(f[1]);
  const lon = Number(f[2]);
  const building = (f[3] ?? '').trim();
  if ((f[16] ?? '').trim() === 'steps' && building === '') {
    const count = Number((f[17] ?? '').trim());
    const incline = (f[18] ?? '').trim().toLowerCase();
    const counted = count > 0 && count < 1000, inclined = incline === 'up' || incline === 'down';
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || f[1] === '' || (!counted && !inclined)) return undefined;
    const steps: StepsTags = {};
    if (counted) steps.count = Math.round(count);
    if (inclined) steps.incline = incline;
    return { lat, lon, building: '', steps };
  }
  if (building === '' && (f[15] ?? '').trim() === 'charging_station') {
    return Number.isFinite(lat) && Number.isFinite(lon) && f[1] !== '' ? { lat, lon, building: '', charger: true } : undefined;
  }
  // A relation without a computable centre, or a stray header or remark
  // line, has no position to match and is worth nothing.
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || f[1] === '' || building === '') return undefined;
  const tags: BuildingTags = { lat, lon, building };
  const height = parseLength(f[4]);
  if (height !== undefined && height > 0) tags.height = height;
  const minHeight = parseLength(f[5]);
  if (minHeight !== undefined && minHeight > 0) tags.minHeight = minHeight;
  const levels = parseLevels(f[6]);
  if (levels !== undefined) tags.levels = levels;
  const roofLevels = parseLevels(f[7]);
  if (roofLevels !== undefined) tags.roofLevels = roofLevels;
  const roofShape = (f[8] ?? '').trim().toLowerCase();
  if (roofShape !== '') tags.roofShape = roofShape;
  const roofHeight = parseLength(f[9]);
  if (roofHeight !== undefined) tags.roofHeight = roofHeight;
  const year = parseYear(f[10]);
  if (year !== undefined) tags.year = year;
  const colour = parseColour(f[11]);
  if (colour !== undefined) tags.colour = colour;
  const roofColour = parseColour(f[12]);
  if (roofColour !== undefined) tags.roofColour = roofColour;
  const material = (f[13] ?? '').trim().toLowerCase();
  if (material !== '') tags.material = material;
  const roofMaterial = (f[14] ?? '').trim().toLowerCase();
  if (roofMaterial !== '') tags.roofMaterial = roofMaterial;
  const amenity = (f[15] ?? '').trim();
  if (amenity !== '') tags.amenity = amenity;
  return tags;
}

/** The rows whose centre falls within `bounds` grown by `margin` degrees. */
export function tagsWithin(tags: readonly BuildingTags[], bounds: TileBounds, margin = 0): BuildingTags[] {
  return tags.filter(t =>
    t.lat <= bounds.north + margin && t.lat >= bounds.south - margin &&
    t.lon >= bounds.west - margin && t.lon <= bounds.east + margin);
}

// --- values ------------------------------------------------------------------

/**
 * A length in metres from an OSM value: `12`, `12 m`, `12,5`, `40'`, `40 ft`,
 * `12'6"`. Out-of-range values — a 2000-metre shed is a typo for 20.00 — come
 * back undefined rather than as a skyscraper.
 */
export function parseLength(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const s = raw.trim().toLowerCase().replace(',', '.');
  if (s === '') return undefined;
  const feet = /^(\d+(?:\.\d+)?)\s*(?:'|ft|feet)\s*(?:(\d+(?:\.\d+)?)\s*(?:"|in))?$/.exec(s);
  let metres: number;
  if (feet !== null) {
    metres = Number(feet[1]) * 0.3048 + (feet[2] !== undefined ? Number(feet[2]) * 0.0254 : 0);
  } else {
    const m = /^(-?\d+(?:\.\d+)?)\s*(m|metres?|meters?)?$/.exec(s);
    if (m === null) return undefined;
    metres = Number(m[1]);
  }
  return Number.isFinite(metres) && metres >= 0 && metres < 900 ? metres : undefined;
}

/**
 * Storeys: `3`, `2.5`, and `3;4` for a building mapped as one outline over
 * two heights, where the taller is the one that shows.
 */
export function parseLevels(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  let best: number | undefined;
  for (const part of raw.split(/[;,]/)) {
    const value = Number(part.trim());
    if (part.trim() !== '' && Number.isFinite(value) && value >= 0 && value <= 200) {
      best = best === undefined ? value : Math.max(best, value);
    }
  }
  return best;
}

/**
 * A single year from `start_date`, which OSM spells many ways: `1929`,
 * `1929-05-12`, `~1900`, `1920s`, `C19` (the 1800s), `early 1900s`,
 * `1890..1910`, `before 1850`. Ranges and decades resolve to their middle.
 */
export function parseYear(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const s = raw.trim().toLowerCase();
  if (s === '') return undefined;
  const century = /^c\s*(\d{1,2})$/.exec(s);
  if (century !== null) return (Number(century[1]) - 1) * 100 + 50;
  const range = /(\d{4})\s*(?:\.\.|–|-(?=\d{4}$))\s*(\d{4})/.exec(s);
  if (range !== null) return Math.round((Number(range[1]) + Number(range[2])) / 2);
  const decade = /(\d{3})0s/.exec(s);
  if (decade !== null) {
    const base = Number(decade[1]) * 10;
    // "1900s" is ambiguous between a decade and a century; OSM uses it for
    // both, and the middle of the decade is wrong by less on average.
    if (s.includes('early')) return base + 2;
    if (s.includes('late')) return base + 8;
    return base + 5;
  }
  const year = /(\d{4})/.exec(s);
  if (year === null) return undefined;
  const value = Number(year[1]);
  return value >= 1000 && value <= 2100 ? value : undefined;
}

/** CSS colour names OSM mappers actually use for walls and roofs. */
const NAMED_COLOURS: Record<string, number> = {
  white: 0xf2f0ea, black: 0x2b2b2b, grey: 0x9a9a9a, gray: 0x9a9a9a,
  lightgrey: 0xc9c9c9, lightgray: 0xc9c9c9, darkgrey: 0x5c5c5c, darkgray: 0x5c5c5c,
  silver: 0xc0c0c0, red: 0xb03a2e, darkred: 0x7a2420, maroon: 0x6e2a24,
  brown: 0x7a5236, darkbrown: 0x4e3524, tan: 0xd2b48c, beige: 0xe6d9b8,
  cream: 0xf1e6c8, ivory: 0xf6f0de, yellow: 0xe8c95a, lightyellow: 0xf3e5a0,
  orange: 0xd98a3a, pink: 0xe8b4b0, salmon: 0xe39a86, green: 0x5f8a58,
  darkgreen: 0x345a36, lightgreen: 0xa8c89a, olive: 0x7c7c48, blue: 0x4f6f9a,
  lightblue: 0x9fbfd8, darkblue: 0x2f4468, navy: 0x2a3658, teal: 0x3f7f7c,
  terracotta: 0xb8633f, ochre: 0xc79a3c, sand: 0xd8c39a, brick: 0x9c4a32,
};

/** A colour as 0xRRGGBB from `#rgb`, `#rrggbb` or a name. */
export function parseColour(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const s = raw.trim().toLowerCase().replace(/[\s_-]/g, '');
  if (s === '') return undefined;
  const hex = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/.exec(s);
  // Bare three-digit hex is left to the names: `#` makes it a colour.
  if (hex !== null && (s.startsWith('#') || hex[1].length === 6)) {
    const h = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1];
    return parseInt(h, 16);
  }
  return NAMED_COLOURS[s];
}
